<template>
  <div
    :class="[
      'mc-wizard-container',
      { 'mc-wizard-container--long': items.length >= 6, 'mc-wizard-container--compact': compact },
    ]"
  >
    <div class="swiper-button-prev"></div>
    <div class="swiper-button-next"></div>
    <div :key="componentKey" ref="swiper" class="swiper">
      <div class="swiper-wrapper">
        <div
          v-for="(item, idx) in items"
          :key="item.key"
          :class="{
            'mc-wizard-item': true,
            'swiper-slide': true,
            active: isActive[item.key],
            done: isActive[item.key] || (hasDone[item.key].curr && !isSingleStatus),
          }"
        >
          <div class="__circle">
            <span v-if="withNumber" class="circle__text">{{ idx + 1 }}</span>
            <i v-else class="mc-icon icon-fill_checkmark"></i>
          </div>

          <div class="__label">
            <div class="label__header">{{ item.label }}</div>
            <div v-if="hasDone[item.key].curr && item.sub_label" class="label__sub-label">
              {{ item.sub_label }}
            </div>
            <div v-if="hasDone[item.key].curr && item.sub_sub_label" class="label__sub-sub-label">
              {{ item.sub_sub_label }}
            </div>
          </div>

          <div
            v-if="compact && idx !== items.length - 1"
            class="__compact-lines"
            :class="{ '__not-completed': hasDone[item.key].next === false || isSingleStatus }"
          />

          <div v-else-if="!compact" class="__lines">
            <template v-if="idx > 0">
              <div class="__not-completed __left" />
              <div v-if="hasDone[item.key].prev" class="__completed __left" />
            </template>
            <template v-if="idx < items.length - 1">
              <div class="__not-completed __right" />
              <div v-if="hasDone[item.key].next" class="__completed __right" />
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Swiper, { Navigation } from 'swiper'
import 'swiper/swiper-bundle.min.css'

export default {
  props: {
    // with format of [{key: '', label: ''}]
    items: {
      required: true,
    },
    // the key of active item in the wizard
    activeKey: {
      default: null,
    },
    withNumber: {
      type: Boolean,
      default: false,
    },
    compact: {
      type: Boolean,
      default: false,
    },
    isSingleStatus: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      swiper: null,
      componentKey: 0,
    }
  },
  computed: {
    // Current active
    isActive() {
      let obj = {}

      this.items.forEach((item) => {
        obj[item.key] = false

        if (this.activeKey == item.key) obj[item.key] = true
      })

      return obj
    },

    // Has been activated before, a.k.a previous state before active
    hasDone() {
      if (this.activeKey == null) return {}
      let obj = {}

      const activeItemIdx = this.items.findIndex((item) => item.key == this.activeKey)

      this.items.map((item, idx) => {
        if (idx < activeItemIdx) {
          return (obj[item.key] = {
            prev: true,
            curr: true,
            next: true,
          })
        }
        obj[item.key] = {
          prev: idx - 1 < activeItemIdx,
          curr: idx == activeItemIdx,
          next: idx + 1 < activeItemIdx,
        }
      })

      return obj
    },
  },
  watch: {
    items() {
      this.componentKey += 1
    },
  },
  mounted() {
    if (this.compact) {
      this.initSwiperCompact()
    } else {
      this.initSwiper()
    }
  },
  updated() {
    if (this.compact) {
      this.initSwiperCompact()
    } else {
      this.initSwiper()
    }
  },
  methods: {
    initSwiper() {
      this.swiper = new Swiper(this.$refs.swiper, {
        modules: [Navigation],
        // get current active state index
        initialSlide: this.items?.findIndex((i) => i.key == this.activeKey) ?? 0,
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        },
        breakpoints: {
          640: {
            slidesPerView: 3,
            spaceBetween: 4,
          },
          1025: {
            slidesPerView: 6,
            spaceBetween: 10,
          },
          1026: {
            slidesPerView: this.items.length,
            spaceBetween: 10,
          },
        },
      })
    },
    initSwiperCompact() {
      this.swiper = new Swiper(this.$refs.swiper, {
        modules: [Navigation],
        initialSlide: this.items?.findIndex((i) => i.key == this.activeKey) ?? 0,
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        },
        slidesPerView: 'auto',
        spaceBetween: 0,
        freeMode: true,
        scrollbar: true,
        mousewheel: true,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
/* wizard container */
.mc-wizard-container {
  position: relative;
  display: flex;

  @media (min-width: 768px) {
    margin-left: -1.5%;
    max-width: 600px;

    &--long {
      max-width: 100%;
    }
  }

  &--compact {
    max-width: none;
    margin: 0;

    .swiper {
      margin: 0;
    }

    :not(.swiper-button-lock) ~ .swiper {
      margin: 0 35px;
    }

    .swiper-button-next,
    .swiper-button-prev {
      top: var(--swiper-navigation-top-offset, 50%);
      height: var(--swiper-navigation-size);
    }

    .swiper-button-next::after,
    .swiper-button-prev::after {
      height: unset;
    }

    .mc-wizard-item {
      flex-direction: row;
      align-items: center !important;
      gap: 8px;
      width: fit-content !important;
      margin-right: 8px !important;
      padding: 0 !important;

      .__circle {
        width: 20px;
        height: 20px;
        padding: auto;

        .circle__text {
          font-size: 11px;
        }
      }

      .__label {
        margin: 0;
      }

      .__compact-lines {
        width: 40px;
        height: 2px;
        background: $mep-primary;

        &.__not-completed {
          background: $gray-400;
        }
      }
    }
  }
}

/* wizard item / slider */
.mc-wizard-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 5px 10px;

  /* blue and gray line to connect state circle  */
  .__lines {
    position: absolute;
    top: 0;
    width: 60%;
    height: 32px;

    /* left side of state circle  */
    & > .__left {
      left: -50%;
    }

    /* right side of state circle  */
    & > .__right {
      right: -50%;
    }

    /* blue line completed */
    .__completed {
      background: $mep-primary;
    }

    /* gray line not completed */
    .__not-completed {
      background: $gray-400;
    }

    /* left and right side */
    & > * {
      position: absolute;
      z-index: -10;
      height: 2.5px;
      top: 20px;
      width: 100%;
    }
  }

  @media (max-width: 1024px) {
    &:first-of-type,
    &:last-of-type {
      .__lines {
        width: 90%;

        & > .__left {
          left: -5%;
        }

        & > .__right {
          right: -5%;
        }
      }
    }

    &:first-of-type {
      align-items: flex-start;
      padding-left: 10%;
    }

    &:last-of-type {
      align-items: flex-end;
      padding-right: 10%;
    }
  }

  /* state label */
  .__label {
    margin-top: 10px;
    font-size: 12px;
    text-align: center;

    .label__header {
      font-size: 13px;
      color: $mep-color-text;
    }
  }

  /* state circle */
  .__circle {
    position: relative;
    z-index: 10;
    display: grid;
    place-items: center;
    border-radius: 100%;
    line-height: 0;
    width: 32px;
    height: 32px;
    background: lighten($gray-400, 10%);
    color: $mep-primary;

    .circle__text,
    .mc-icon:before {
      color: $teal-300;
      font-size: 16px;
    }
  }

  /* blue circle background on done */
  &.done .__circle {
    background: $mep-primary;
    color: $white;

    & > .circle__text,
    & > .mc-icon:before {
      color: $white;
    }
  }

  &.done .__label {
    .label__header {
      font-weight: 600;
    }
    .label__sub-sub-label {
      color: $teal-300;
    }
  }
}

/* base swiper button arrow */
.swiper-button-prev,
.swiper-button-next {
  top: 30%;
  height: 100%;

  &:after {
    display: grid;
    place-items: center;
    font-size: 16px;
    background: $gray-400;
    border-radius: 5px;
    color: $primary;
    font-weight: 600;
    height: 100%;
    padding: 10px;
  }
}

/* align to left */
.swiper-button-prev {
  left: 0;
}

/* align to right */
.swiper-button-next {
  right: 0;
}

.mc-wizard-container:not(.mc-wizard-container--compact) {
  .swiper {
    width: 100%;
  }
}
</style>
